// Currency-bound formatters. Volkan ratify 2026-06-02 (A): currency
// display is currency-bound, NOT user-locale-aware. USD always uses
// en-US separator ($1,234.56); TRY always tr-TR (1.234,56 ₺); EUR de-DE;
// GBP en-GB; JPY ja-JP; KWD ar-KW.
//
// Day 1 codemod (9800e91) migrated currency formatters to intlLocale()
// (user-locale-aware) which caused TR-selected users to see USD as
// "1.234,56 $" (Turkish separator). This module reverses that for the
// currency-context call sites only; date/time/qty intlLocale() remain
// user-locale-aware.
//
// CI guard `locale-format.test.ts` allowlists this file (the currency
// formatter map intentionally hardcodes locale strings per currency,
// not per user locale).

import { PRICE_MASK, pricesMasked } from './price-mask'

const CURRENCY_LOCALES: Record<string, string> = {
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  TRY: 'tr-TR',
  JPY: 'ja-JP',
  KWD: 'ar-KW',
}

export function currencyLocale(currency: string): string {
  return CURRENCY_LOCALES[currency] ?? 'en-US'
}

export function formatCurrency(
  amount: number,
  currency: string = 'USD',
  options?: Intl.NumberFormatOptions,
): string {
  // Null = the server withheld the amount (hide-prices mode) — never crash.
  if (pricesMasked() || typeof amount !== 'number' || !Number.isFinite(amount)) return PRICE_MASK
  return amount.toLocaleString(currencyLocale(currency), {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  })
}

export function formatCurrencyAmount(
  amount: number,
  currency: string = 'USD',
  options?: Intl.NumberFormatOptions,
): string {
  // Defensive: a missing/NaN amount must never crash the screen that renders it
  // (e.g. a balance card before its value loads). Callers that want a dash for
  // "no value" should guard before calling; here we never throw.
  if (pricesMasked()) return PRICE_MASK
  const safe = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0
  return safe.toLocaleString(currencyLocale(currency), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  })
}
